import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Phone } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { CONTACT } from "../data/gear";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "", email: "", phone: "", event_date: "",
  location: "", items: "", message: "",
};

export const QuoteDialog = ({ open, onOpenChange, category = "general", presetItem = "" }) => {
  const [form, setForm] = useState({ ...empty });
  const [loading, setLoading] = useState(false);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, {
        ...form,
        category,
        items: presetItem ? `${presetItem}${form.items ? " — " + form.items : ""}` : form.items,
      });
      toast.success("Request sent! We'll reach out shortly to confirm your quote.");
      setForm({ ...empty });
      onOpenChange(false);
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + CONTACT.phone);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quote-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto bg-[#F9F7F2]">
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl font-bold text-primary">
            Request a Quote
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Tell us about your event and we'll send a custom quote. Setup &amp; teardown is always included.
            {presetItem && (
              <span className="block mt-1 text-accent font-medium">Interested in: {presetItem}</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name *" testid="quote-name" value={form.name} onChange={change("name")} placeholder="Jane Doe" />
            <Field label="Phone *" testid="quote-phone" value={form.phone} onChange={change("phone")} placeholder="(850) 555 0123" />
          </div>
          <Field label="Email *" testid="quote-email" type="email" value={form.email} onChange={change("email")} placeholder="you@email.com" />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Event Date" testid="quote-date" type="date" value={form.event_date} onChange={change("event_date")} />
            <Field label="Location" testid="quote-location" value={form.location} onChange={change("location")} placeholder="Destin, FL" />
          </div>
          <Field label="Gear of interest" testid="quote-items" value={form.items} onChange={change("items")} placeholder="2 speakers, projector, 6 beach chairs..." />
          <div className="space-y-1.5">
            <Label className="text-sm text-primary">Message</Label>
            <Textarea
              data-testid="quote-message"
              value={form.message}
              onChange={change("message")}
              placeholder="Tell us about your event..."
              className="bg-white border-border min-h-[90px]"
            />
          </div>

          <button
            type="submit"
            data-testid="quote-submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-accent text-accent-foreground font-semibold uppercase tracking-widest text-sm hover:bg-accent/90 transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
            {loading ? "Sending..." : "Send Request"}
          </button>

          <a
            href={CONTACT.phoneHref}
            className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <Phone className="w-4 h-4" /> Prefer to call? {CONTACT.phone}
          </a>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, testid, ...props }) => (
  <div className="space-y-1.5">
    <Label className="text-sm text-primary">{label}</Label>
    <Input data-testid={testid} {...props} className="bg-white border-border" />
  </div>
);
